import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';

import { BadgeComponent } from '@shared/components/badge/badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  ActionType,
  GqlService,
  listCloseSummaryNotesQuery,
  listUserNamesWithEmailQuery,
  ModifyCloseSummaryNote,
  User,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { NoteListItem, QcReviewNotesModalInput } from './qc-review-notes-modal.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    FormsModule,
    NgSelectComponent,
    ReactiveFormsModule,
    NgOptionComponent,
    NgClass,
    BadgeComponent,
    TooltipDirective,
    IconComponent,
    DatePipe,
  ],
  selector: 'aux-qc-review-notes-modal',
  styleUrl: 'qc-review-notes-modal.component.scss',
  templateUrl: 'qc-review-notes-modal.component.html',
})
export class QcReviewNotesModalComponent {
  changeDetectorRef = inject(ChangeDetectorRef);
  ref = inject(CustomOverlayRef<unknown, QcReviewNotesModalInput>);

  private authQuery = inject(AuthQuery);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  loading = signal(true);

  filteredNoteList: NoteListItem[] = [];
  initialNoteList: NoteListItem[] = [];
  monthFilterControl = new FormControl();
  monthOptions: Option[] = [];
  notes = '';
  removedNoteList: string[] = [];
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();

  private canDeactivate = async () => {
    if (this.notes.length > 0 || this.removedNoteList.length > 0) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await result.afterClosed$.toPromise();

      return !!event?.data;
    }

    return true;
  };

  constructor() {
    this.ref.canDeactivate = this.canDeactivate;
    this.initMonthOptions();
    this.initNoteList();
    this.onFilterChange();

    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((users) => {
        users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });

    this.gqlService
      .listCloseSummaryNotes$({
        cost_category: this.ref.data.cost_category,
        current_quarter: this.ref.data?.current_quarter,
        group_value: this.ref.data.group_value,
        vendor_id: this.ref.data?.vendor_id,
      })
      .subscribe((response) => {
        if (response.data) {
          response.data.forEach((note) => {
            const targetMonth = this.initialNoteList.find(
              (noteListItem) => noteListItem.month === note.timeline_month
            );

            if (targetMonth) {
              const user = this.users.get(note.created_by);

              targetMonth.items.push({
                ...note,
                removable: this.isNoteRemovable(note),
                username: `${user?.given_name} ${user?.family_name}`,
              });
            }
          });

          this.initialNoteList.forEach((listItem) => {
            listItem.items.sort((a, b) => {
              return new Date(b.create_date).getTime() - new Date(a.create_date).getTime();
            });
          });

          this.filteredNoteList = [...this.initialNoteList];
          this.changeDetectorRef.detectChanges();
        }

        this.loading.set(false);
      });
  }

  initMonthOptions(): void {
    this.monthOptions = this.ref.data?.selectedQuarterMonths.map((month: string) => ({
      label: dayjs(month).format('MMMM'),
      value: month,
    }));

    if (this.monthOptions.length > 1) {
      this.monthOptions.unshift({ label: 'All', value: '' });
      this.monthFilterControl.setValue('');
    } else {
      this.monthFilterControl.setValue(this.monthOptions[0].value);
      this.monthFilterControl.disable();
    }
  }

  initNoteList(): void {
    this.initialNoteList = this.ref.data?.selectedQuarterMonths
      .map((month: string) => ({
        items: [],
        label: dayjs(month).format('MMMM YYYY'),
        month: month,
      }))
      .reverse();
  }

  isNoteRemovable(note: listCloseSummaryNotesQuery): boolean {
    if (!this.ref.data?.isCurrentQuarterSelected) {
      return false;
    }

    const currentUserID = this.authQuery.getValue().sub;

    return (
      dayjs(note.timeline_month).isSame(dayjs(this.mainQuery.currentOpenMonth())) &&
      currentUserID === note.created_by
    );
  }

  isSaveBtnDisabled(): boolean {
    return !this.notes.length && this.removedNoteList.length === 0;
  }

  onFilterChange(): void {
    this.monthFilterControl.valueChanges.subscribe((value) => {
      if (value === '') {
        this.filteredNoteList = [...this.initialNoteList];
      } else {
        this.filteredNoteList = this.initialNoteList.filter(
          (noteListItem: NoteListItem) => noteListItem.month === value
        );
      }
    });
  }

  onSaveClick(): void {
    const notesChangesToSave: ModifyCloseSummaryNote[] = [];

    this.removedNoteList.forEach((noteId) => {
      notesChangesToSave.push({
        action_type: ActionType.ACTION_TYPE_REMOVE,
        group_value: this.ref.data?.group_value,
        id: noteId,
        vendor_id: this.ref.data?.vendor_id,
      });
    });

    if (this.notes.length > 0) {
      notesChangesToSave.push({
        action_type: ActionType.ACTION_TYPE_CREATE,
        cost_category: this.ref.data.cost_category,
        group_value: this.ref.data.group_value,
        id: Utils.uuid(),
        message: this.notes,
        vendor_id: this.ref.data?.vendor_id,
      });
    }

    this.loading.set(true);

    this.gqlService.batchModifyCloseSummaryNotes$(notesChangesToSave).subscribe((response) => {
      if (response.success) {
        this.overlayService.success('Changes successfully saved!');
        this.notes = '';
        this.removedNoteList = [];
        this.ref.close({
          notesCount:
            response.data?.filter(
              (note) =>
                note.group_value === this.ref.data.group_value &&
                note.vendor_id === this.ref.data?.vendor_id &&
                note.cost_category === this.ref.data.cost_category
            ).length || 0,
        });
      } else {
        this.overlayService.error(response.errors);
      }
    });
  }

  removeNote(
    note: listCloseSummaryNotesQuery & {
      removable: boolean;
      username: string;
    },
    monthIndex: number
  ): void {
    this.filteredNoteList[monthIndex].items = this.filteredNoteList[monthIndex].items.filter(
      (item) => item.id !== note.id
    );

    this.removedNoteList.push(note.id);
  }

  saveButtonTooltip(): string {
    return this.isSaveBtnDisabled() ? MessagesConstants.NO_CHANGES_TO_SAVE : '';
  }
}
